import {
  ExecutedMessage,
  SentMessage,
  SentMessage_filter,
} from "@/../.graphclient";
import chainIdMap from "@/lib/chainIdMap";
import { SourceChain } from "@/lib/chains";
import { graphSDK } from "@/lib/graphSDK";
import axios from "axios";
import CHAINS, { DestinationChain, DESTINATION_CHAINS } from "lib/chains";
import { getSubgraphName } from "lib/subgraphs";
import { MessageTableFilters } from "lib/types";
import { useEffect, useState } from "react";
import { useImmer } from "use-immer";

function convertFiltersToGraphQL(
  filters: MessageTableFilters,
  destinationChainCount: number
) {
  let whereClause: SentMessage_filter = {};
  if (filters.chain !== null && filters.chain.length < destinationChainCount) {
    whereClause.messageReceiverChainID_in = filters.chain.map(
      (c) => CHAINS[c].chainId
    );
  }
  if (filters.origin !== null && filters.origin !== "") {
    whereClause.transactionOrigin = filters.origin;
  }
  if (filters.source !== null && filters.source !== "") {
    whereClause.messageSender = filters.source;
  }
  if (filters.target !== null && filters.target !== "") {
    whereClause.messageReceiver = filters.target;
  }
  if (filters.startDate !== null) {
    whereClause.blockTimestamp_gte = filters.startDate.getTime() / 1000;
  }
  if (filters.endDate !== null) {
    whereClause.blockTimestamp_lte = filters.endDate.getTime() / 1000;
  }
  return whereClause;
}

export type SentMessagesResult = (SentMessage | null)[];

function filtersAreDefault(
  filters: MessageTableFilters,
  destinationChainCount: number
) {
  return (
    (filters.chain === null ||
      filters.chain.length === destinationChainCount) &&
    (filters.origin === null || filters.origin === "") &&
    (filters.source === null || filters.source === "") &&
    (filters.target === null || filters.target === "") &&
    filters.startDate === null &&
    filters.endDate === null
  );
}

export function useSentMessagesQuery(
  page: number,
  count: number,
  filters: MessageTableFilters,
  sourceChain: SourceChain
): [SentMessagesResult, boolean] {
  const [results, setResults] = useState<(SentMessage | null)[]>([]);
  const [loading, setLoading] = useState(false);
  const destinationChainCount = DESTINATION_CHAINS[sourceChain].length;
  useEffect(() => {
    async function fetchMessages() {
      setLoading(true);
      const where = convertFiltersToGraphQL(filters, destinationChainCount);
      const res = await graphSDK.GetSentMessages(
        { where, skip: (page - 1) * count, count: count },
        { subgraphName: getSubgraphName(sourceChain) }
      );
      const sentMessages = res.sentMessages as SentMessage[];
      if (sentMessages.length === 0) {
        setLoading(false);
        setResults([]);
      }

      if (filtersAreDefault(filters, destinationChainCount)) {
        // In the case that the subgraph errored (due to bug in indexer code) and rows are missing,
        // fill those rows with null so that the results array is contiguous by nonce
        // We can only do this when there are no filters (and we expect a contiguous result)
        let filledMessages: (SentMessage | null)[] = [];
        let sentMessageIndex = 0;
        let maxNonce = parseInt(sentMessages[0]?.messageNonce);
        for (
          let filledMessageIndex = 0;
          filledMessageIndex < count && maxNonce - filledMessageIndex > 0;
          ++filledMessageIndex
        ) {
          if (
            sentMessages[sentMessageIndex] !== undefined &&
            parseInt(sentMessages[sentMessageIndex]?.messageNonce) ===
              maxNonce - filledMessageIndex
          ) {
            filledMessages.push(
              sentMessages[sentMessageIndex++] as SentMessage
            );
          } else {
            filledMessages.push(null);
          }
        }
        setResults(filledMessages);
      } else {
        setResults(sentMessages);
      }
      setLoading(false);
    }
    fetchMessages();
  }, [page, count, filters, sourceChain]);
  return [results, loading];
}

export type ExecutedMessagesResult = Map<number, ExecutedMessage>;

export function useExecutedMessagesQuery(
  sentMessages: (SentMessage | null)[],
  sourceChain: SourceChain
): [ExecutedMessagesResult, boolean] {
  const [results, setResults] = useImmer<Map<number, ExecutedMessage>>(
    new Map()
  );
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    async function fetchMessages(destinationChain: DestinationChain) {
      setLoading(true);
      const where = {
        messageSenderChainID: CHAINS[sourceChain].chainId,
        messageNonce_in: sentMessages
          .filter((msg) => msg !== undefined && msg !== null)
          .map((msg) => msg?.messageNonce),
      };
      const res = await graphSDK.GetExecutedMessages(
        { where, skip: 0, count: sentMessages.length },
        { subgraphName: getSubgraphName(destinationChain) }
      );
      const executedMessages = res.executedMessages as ExecutedMessage[];

      setResults((map) => {
        executedMessages.map((msg) => map.set(msg.messageNonce, msg));
      });
    }
    async function fetchAllMessages() {
      setLoading(true);
      await Promise.all(
        DESTINATION_CHAINS[sourceChain].map((c) => fetchMessages(c)) || []
      );
      setLoading(false);
    }

    setResults((map) => map.clear());
    if (sentMessages.length > 0) fetchAllMessages();
  }, [sentMessages, sourceChain]);
  return [results, loading];
}

type LatestHeadUpdatesResult = [Map<DestinationChain, number>, boolean];

// Gets the latest head updates on each destination chain, then calculates the corresponding block number from slot number
export function useLatestHeadUpdates(
  sourceChain: SourceChain
): LatestHeadUpdatesResult {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useImmer<Map<DestinationChain, number>>(
    new Map()
  );
  useEffect(() => {
    async function fetchHeadUpdates() {
      setLoading(true);
      setResults(new Map());
      const promises = DESTINATION_CHAINS[sourceChain].map(
        async (destinationChain) => {
          const res = await graphSDK.GetRecentHeadUpdates(
            {
              sourceChain: CHAINS[sourceChain].chainId,
              first: 1,
              skip: 0,
            },
            { subgraphName: getSubgraphName(destinationChain) }
          );
          // TODO: this fails when no head updates have been sent yet
          return parseInt(res.headUpdates[0]?.slot || "0");
        }
      );
      const slotNumbers = await Promise.all(promises);
      const blockNumberRes = await axios.post("/api/getBlockNumbers", {
        slots: slotNumbers,
        sourceChain: sourceChain,
      });
      setResults((map) => {
        DESTINATION_CHAINS[sourceChain].map((chain, idx) =>
          map.set(chain as DestinationChain, blockNumberRes.data[idx])
        );
      });
      setLoading(false);
    }
    fetchHeadUpdates();
  }, [sourceChain]);
  return [results, loading];
}

export function useLastFinalizedSlot(sourceChain: SourceChain): number | null {
  const [slot, setSlot] = useState<number | null>(null);
  useEffect(() => {
    async function fetchNodeData() {
      setSlot(null);
      const res = await fetch(
        CHAINS[sourceChain].nodeUrl + "eth/v1/beacon/headers/finalized"
      );
      const json = await res.json();
      setSlot(parseInt(json.data.header.message.slot));
    }
    fetchNodeData();
  }, [sourceChain]);
  return slot;
}

export function useLastFinalizedBlock(sourceChain: SourceChain): number | null {
  const slot = useLastFinalizedSlot(sourceChain);
  const [block, setBlock] = useState(null);
  useEffect(() => {
    async function fetchBlockNumber() {
      const blockNumberRes = await axios.post("/api/getBlockNumbers", {
        slots: [slot],
        sourceChain: sourceChain,
      });
      setBlock(blockNumberRes.data[0]);
    }
    fetchBlockNumber();
  }, [sourceChain, slot]);
  return block;
}
